// Die Angaben einer neuen Organisation aus einer Signatur (ST-PTY-001, Signatur S2,
// Vorgabe DESIGN V2).
//
// WARUM AUF DEM REST UND NICHT AUF DEM FREITEXT: free-text.ts zerlegt die
// Signatur schon in Stuecke und legt alles, was keinem Feld der Person passt,
// in den Rest. Genau diese Stuecke sieht die Nutzerin unter "Nicht
// übernommen". Wer hier ein Stueck uebernimmt, nennt es in `taken`, und der
// Bildschirm nimmt es aus dem Rest (X6). Eine zweite Zerlegung des
// Rohtexts liefe auseinander, und ein uebernommenes Stueck stuende dann
// zugleich im Feld und im Rest.
//
// Die Form prueft der Server (Vertrag #746): Hier wird nur erkannt und
// vorbefuellt, nie abgelehnt. Was nicht eindeutig passt, bleibt im Rest.

import { countryName } from './addresses.ts';
import type { UnassignedFragment } from './free-text.ts';

/** Die Registerarten der Auswahl, in der Schreibweise des Servers. */
export const REGISTER_ARTEN = Object.freeze(['HRA', 'HRB', 'GnR', 'PR', 'VR'] as const);
export type RegisterArt = (typeof REGISTER_ARTEN)[number];

export interface OrganisationAngaben {
  readonly website: string;
  readonly registerArt: RegisterArt | '';
  readonly registerNummer: string;
  readonly registerGericht: string;
  readonly ustId: string;
}

export const LEERE_ORGANISATION_ANGABEN: OrganisationAngaben = Object.freeze({
  website: '',
  registerArt: '',
  registerNummer: '',
  registerGericht: '',
  ustId: '',
});

export interface ErkannteOrganisationAngaben {
  readonly angaben: OrganisationAngaben;
  /** Die Reststuecke, die in ein Feld gingen, im Wortlaut. */
  readonly taken: readonly string[];
}

export const KEINE_ERKANNTEN_ANGABEN: ErkannteOrganisationAngaben = Object.freeze({ angaben: LEERE_ORGANISATION_ANGABEN, taken: Object.freeze([]) });

// Die Beschriftungen gelten nur am Anfang eines Stuecks. Ein Doppelpunkt danach ist erlaubt.
const WEBSITE_LABEL = /^(?:Website|Webseite|Web|Internet|Homepage)(?![\p{L}.])\s*:?\s*/iu;
const REGISTER_LABEL = /^(?:Handelsregister(?:nummer)?|Registernummer|Register)(?![\p{L}])\s*:?\s*/iu;
const GERICHT_LABEL = /^(?:Registergericht|Register\s*gericht)\s*:?\s*/iu;
// "USt-IdNr.", "USt.-IdNr.", "USt-ID", "USt-Id-Nr.", "UID", "VAT", "VAT-ID" sind dasselbe Feld (V2).
const UST_LABEL = /^(?:USt\.?[-\s]?Id(?:[-\s]?Nr)?\.?|UID|VAT(?:[-\s]?(?:ID|No|Number))?\.?)\s*:?\s*/iu;

// Host mit mindestens einem Punkt, optional ein Pfad. Kein Leerzeichen, kein "@".
const WEBSITE = /^(?:https?:\/\/)?((?:[\p{L}\p{N}-]+\.)+[\p{L}]{2,}(?:\/[^\s@]*)?)$/iu;
const REGISTER = /\b(HRA|HRB|GnR|PR|VR)\s*(\d+(?:\s?[A-Z])?)\b/iu;
const AMTSGERICHT = /\bAmtsgericht\s+[\p{L}][\p{L} .-]*[\p{L}.]/u;
// Zwei Buchstaben, dann Ziffern oder Buchstaben mit Leerzeichen, Punkten oder Strichen dazwischen.
const UST = /^([A-Z]{2})((?:[\s.-]?[0-9A-Z]){2,13})$/u;

function websiteAus(text: string): string | undefined {
  const ohneLabel = text.replace(WEBSITE_LABEL, '').trim();
  const treffer = WEBSITE.exec(ohneLabel);
  if (treffer === null) return undefined;
  // Ohne Beschriftung muss es nach einer Adresse aussehen; "Muster.GmbH" ist keine.
  if (ohneLabel === text.trim() && !/^(?:https?:\/\/|www\.)/iu.test(ohneLabel)) return undefined;
  return (treffer[1] ?? '').replace(/\/$/u, '');
}

function ustIdAus(text: string): string | undefined {
  const ohneLabel = text.replace(UST_LABEL, '');
  // Ohne Beschriftung ist eine Buchstaben-Ziffern-Folge zu vieldeutig.
  if (ohneLabel === text) return undefined;
  const treffer = UST.exec(ohneLabel.trim());
  return treffer === null ? undefined : `${treffer[1]}${(treffer[2] ?? '').replace(/[\s.-]/gu, '')}`;
}

interface RegisterTeile {
  readonly art?: RegisterArt;
  readonly nummer?: string;
  readonly gericht?: string;
}

/**
 * Registerart, Nummer und Gericht aus einem Stueck. Ein Stueck gilt nur dann als
 * Registerangabe, wenn ausser Beschriftung, Treffern und Trennzeichen nichts
 * uebrig bleibt; sonst bliebe ein Teil der Zeile ungesehen liegen.
 */
function registerAus(text: string): RegisterTeile | undefined {
  let rest = text.replace(GERICHT_LABEL, '').replace(REGISTER_LABEL, '');
  const register = REGISTER.exec(rest);
  if (register !== null) rest = rest.replace(register[0], ' ');
  const amtsgericht = AMTSGERICHT.exec(rest);
  let gericht = amtsgericht?.[0];
  if (amtsgericht !== null) rest = rest.replace(amtsgericht[0], ' ');
  // "Registergericht Hannover": das Gericht ohne das Wort "Amtsgericht".
  if (gericht === undefined && GERICHT_LABEL.test(text) && register === null) {
    gericht = rest.trim();
    rest = '';
  }
  if (register === null && gericht === undefined) return undefined;
  if (rest.replace(/[\s,;:/]/gu, '') !== '') return undefined;
  return {
    ...(register === null
      ? {}
      : {
          art: REGISTER_ARTEN.find((art) => art.toLowerCase() === (register[1] ?? '').toLowerCase()) ?? 'HRB',
          nummer: (register[2] ?? '').replace(/\s/gu, ' ').toUpperCase(),
        }),
    ...(gericht === undefined || gericht === '' ? {} : { gericht: gericht.trim() }),
  };
}

/**
 * Was aus dem Rest als Angabe einer Organisation taugt. Je Feld zaehlt der
 * erste Fund; ein zweiter bleibt im Rest, wie eine zweite E-Mail-Adresse.
 */
export function organisationAngabenAusRest(unassigned: readonly UnassignedFragment[]): ErkannteOrganisationAngaben {
  let angaben: OrganisationAngaben = LEERE_ORGANISATION_ANGABEN;
  const taken: string[] = [];
  for (const { text, reason } of unassigned) {
    if (reason !== 'not_recognized') continue;
    const website = angaben.website === '' ? websiteAus(text) : undefined;
    if (website !== undefined) {
      angaben = { ...angaben, website };
      taken.push(text);
      continue;
    }
    const ustId = angaben.ustId === '' ? ustIdAus(text) : undefined;
    if (ustId !== undefined) {
      angaben = { ...angaben, ustId };
      taken.push(text);
      continue;
    }
    const register = registerAus(text);
    if (register === undefined) continue;
    const nimmtNummer = register.art !== undefined && angaben.registerNummer === '';
    const nimmtGericht = register.gericht !== undefined && angaben.registerGericht === '';
    // Ein Stueck wird nur ganz uebernommen; was schon belegt ist, laesst es im Rest.
    if ((register.art !== undefined && !nimmtNummer) || (register.gericht !== undefined && !nimmtGericht)) continue;
    angaben = {
      ...angaben,
      ...(nimmtNummer ? { registerArt: register.art, registerNummer: register.nummer ?? '' } : {}),
      ...(nimmtGericht ? { registerGericht: register.gericht } : {}),
    };
    taken.push(text);
  }
  return { angaben, taken };
}

/** Wie viele Angaben erkannt sind, fuer den Titel "Angaben zur Organisation (3 erkannt)" (V1). */
export function erkannteAngabenZahl(angaben: OrganisationAngaben, mitAnschrift: boolean): number {
  return (
    (mitAnschrift ? 1 : 0) +
    (angaben.website.trim() === '' ? 0 : 1) +
    (angaben.registerNummer.trim() === '' && angaben.registerGericht.trim() === '' ? 0 : 1) +
    (angaben.ustId.trim() === '' ? 0 : 1)
  );
}

/** Ein Feldpfad der Anlage, der zu den Angaben der Organisation in der Karte "Neu" gehoert. */
export function isOrgAngabePfad(fieldPath: string): boolean {
  return /^(?:address|website|commercialRegister|vatId)(?:\.|$)/u.test(fieldPath);
}

/**
 * An einer vorhandenen Organisation schreibt die Anlage nichts (Vorgabe §1):
 * ihre erkannten Angaben bleiben im Rest, mit dem Grund, wo sie zu pflegen sind.
 */
export function alsAngabenDerOrganisation(unassigned: readonly UnassignedFragment[], taken: readonly string[]): readonly UnassignedFragment[] {
  if (taken.length === 0) return unassigned;
  const offen = new Set(taken);
  return unassigned.map((fragment) =>
    fragment.reason === 'not_recognized' && offen.delete(fragment.text) ? { ...fragment, reason: 'belongs_to_organisation' as const } : fragment,
  );
}

/** Der Grund mit dem Namen der Organisation: „Gehört zu <Organisation>. Pflege es dort.“ in der Form des Rests. */
export function organisationsGrund(organisation: string): string {
  return `gehört zu ${organisation}, pflege es dort`;
}

/** Der Zusatz der Zusammenfassung, wenn die Angaben zur neuen Organisation gehen (DESIGN zu #773). */
export function organisationAngabenGefunden(zahl: number): string {
  return `${zahl === 1 ? 'eine Angabe' : `${zahl} Angaben`} zur Organisation, unten zum Prüfen.`;
}

/** Die Leiste, wenn die neue Organisation an einer Angabe scheitert (V4): kurz, ohne zweiten Schritt. */
export function organisationAngabenAbgelehnt(zahl: number): string {
  return `${zahl === 1 ? 'Eine Angabe ist' : `${zahl} Angaben sind`} unten markiert. Es wurde nichts angelegt.`;
}

/**
 * Die Angaben der Organisation so, wie der Server sie fuer den Fingerprint liest
 * (Vertrag #746, ADR-0060), fuer den Schluessel der Absendekennung (Codex-P2 #773):
 * gleichwertige Schreibweisen sind dieselbe Sendung und rufen das Original ab.
 * Website ohne Protokoll und Schlussstrich, Host klein; USt-IdNr. ohne Leerzeichen,
 * Punkte und Striche in Grossschrift; Handelsregister als "<Art> <Nummer>".
 */
export function organisationAngabenFuerSchluessel(body: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const website = typeof body['website'] === 'string' ? body['website'] : undefined;
  const vat = body['vatId'] as Readonly<Record<string, unknown>> | undefined;
  const register = body['commercialRegister'] as Readonly<Record<string, unknown>> | undefined;
  const registerWert = typeof register?.['value'] === 'string' ? register['value'] : undefined;
  const art = registerWert === undefined ? undefined : REGISTER.exec(registerWert.replace(/\s+/gu, ' ').trim());
  const kanonischeArt = art === null || art === undefined ? undefined : REGISTER_ARTEN.find((name) => name.toLowerCase() === (art[1] ?? '').toLowerCase());
  return {
    ...body,
    ...(website === undefined
      ? {}
      : {
          website: (() => {
            const ohne = website.trim().replace(/^https?:\/\//iu, '').replace(/\/$/u, '');
            const schnitt = ohne.indexOf('/');
            return schnitt < 0 ? ohne.toLowerCase() : `${ohne.slice(0, schnitt).toLowerCase()}${ohne.slice(schnitt)}`;
          })(),
        }),
    ...(vat === undefined || typeof vat['value'] !== 'string' ? {} : { vatId: { ...vat, value: vat['value'].replace(/[\s.-]/gu, '').toUpperCase() } }),
    ...(register === undefined || registerWert === undefined || kanonischeArt === undefined || art === null || art === undefined
      ? {}
      : { commercialRegister: { ...register, value: `${kanonischeArt} ${(art[2] ?? '').toUpperCase()}` } }),
  };
}

/** Der Rest ohne die Stuecke, die in ein Feld der Organisation gingen (X6). */
export function ohneUebernommeneAngaben(unassigned: readonly UnassignedFragment[], taken: readonly string[]): readonly UnassignedFragment[] {
  if (taken.length === 0) return unassigned;
  const offen = new Set(taken);
  return unassigned.filter((fragment) => !(fragment.reason === 'not_recognized' && offen.delete(fragment.text)));
}

/**
 * Die Felder der Anfrage an `POST /api/parties` fuer die Organisation (Vertrag
 * #746). Ein leeres Feld fehlt; `vatId.issuer` wird nie geschickt, die
 * ausstellende Stelle leitet der Server ab.
 */
export function organisationAngabenRumpf(angaben: OrganisationAngaben): Record<string, unknown> {
  const website = angaben.website.trim();
  const nummer = angaben.registerNummer.trim();
  const gericht = angaben.registerGericht.trim();
  const ustId = angaben.ustId.trim();
  const registerWert = [angaben.registerArt, nummer].filter((teil) => teil !== '').join(' ');
  return {
    ...(website === '' ? {} : { website }),
    // Das Gericht ist Pflicht, sobald eine Nummer da ist; fehlt es, sagt der Server es am Feld.
    ...(nummer === '' && gericht === ''
      ? {}
      : { commercialRegister: { ...(gericht === '' ? {} : { issuer: gericht }), ...(registerWert === '' ? {} : { value: registerWert }) } }),
    ...(ustId === '' ? {} : { vatId: { value: ustId } }),
  };
}

/** Die Teile der Anschrift, die die Vorschau braucht; dieselben Felder wie im Formular. */
export interface VorschauAnschrift {
  readonly countryCode: string;
  readonly addressLine1: string;
  readonly postalCode: string;
  readonly locality: string;
}

/**
 * Die gedaempften Zeilen unter der neuen Organisation in "Das wird angelegt"
 * (Vorgabe Abschnitt 3): je erkannter und nicht geleerter Angabe eine,
 * hoechstens vier. Die Anschrift einzeilig mit Komma, das Land nur ausserhalb
 * Deutschlands.
 */
export function organisationVorschauZeilen(angaben: OrganisationAngaben, anschrift: VorschauAnschrift | undefined): readonly string[] {
  const zeilen: string[] = [];
  if (anschrift !== undefined) {
    const ort = [anschrift.postalCode.trim(), anschrift.locality.trim()].filter((teil) => teil !== '').join(' ');
    const land = anschrift.countryCode === '' || anschrift.countryCode === 'DE' ? '' : countryName(anschrift.countryCode);
    const teile = [anschrift.addressLine1.trim(), ort, land].filter((teil) => teil !== '');
    if (teile.length > 0) zeilen.push(`Geschäftsanschrift · ${teile.join(', ')}`);
  }
  if (angaben.website.trim() !== '') zeilen.push(`Website · ${angaben.website.trim()}`);
  const register = [angaben.registerArt, angaben.registerNummer.trim()].filter((teil) => teil !== '').join(' ');
  const gericht = angaben.registerGericht.trim();
  if (angaben.registerNummer.trim() !== '' || gericht !== '') {
    zeilen.push(angaben.registerNummer.trim() === '' ? `Handelsregister · ${gericht}` : [register, gericht].filter((teil) => teil !== '').join(' · '));
  }
  if (angaben.ustId.trim() !== '') zeilen.push(`USt-IdNr. ${angaben.ustId.trim()}`);
  return zeilen;
}
